import { PageTransition } from '@/constant/transition'
import { fetchOccupations, type OccupationCategory } from '@/util/ref'
import { useMutation, useQuery } from '@tanstack/react-query'
import Button from 'react-bootstrap/Button'
import Container from 'react-bootstrap/esm/Container'
import Stack from 'react-bootstrap/Stack'
import { useNavigate } from 'react-router-dom'
import { syncOccupations } from '../helper'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { useEffect, type JSX } from 'react'
import Card from 'react-bootstrap/Card'
import { cardStyle, active } from './style.css'
import { useSelectable } from '@/hook'
import clsx from 'clsx'
import { ProgressFlags } from '@/util/progress'
import { syncProgress } from '../helper'
// import { occupationIdsAtom } from './data'
import { fetchProfileOccupations } from './data'
import type { Occupation } from './data'
import { queryClient } from '@/util/queryClient'

export default function OccupationSelection() {
  const navigate = useNavigate()
  const { data: occupations } = useQuery<Occupation[]>({
    queryKey: ['profile', 'occupation'],
    queryFn: fetchProfileOccupations,
    staleTime: 1000 * 60 * 5,
  })
  const {
    data: occupationCategories,
    isLoading,
    isError,
  } = useQuery<OccupationCategory[]>({
    queryKey: ['occupation'],
    queryFn: fetchOccupations,
    staleTime: 1000 * 60 * 5,
  })
  const [selectedOccupationIds, toggle, reset] = useSelectable<number>([])
  const occupationsMutation = useMutation({
    mutationFn: (occupationIds: number[]) => syncOccupations(occupationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', 'occupation'] })
      syncProgress(ProgressFlags.JOB_PREFS_SELECTED)
      navigate('/onboarding/interest', {
        state: { direction: PageTransition.FromRight },
      })
    },
    onError: (err) => console.log(err),
  })

  useEffect(() => {
    reset(occupations ? occupations.map(({ id }) => id) : [])
  }, [reset, occupations])

  return (
    <Container>
      <h1>志望職種の選択</h1>
      <p className="text-muted small">
        関心のある職種を3つ以上選択してください。あなたの志望に合った企業をおすすめします。
      </p>
      <p className="text-muted small">選択済み: {selectedOccupationIds.length}/3（最低）</p>
      {isError ? (
        <option>error</option>
      ) : isLoading ? (
        <option>Loading...</option>
      ) : (
        <Container>
          {occupationCategories?.reduce<JSX.Element[]>((acc, cur, i, arr) => {
            const next = arr[i + 1]
            if (i % 2 === 0) {
              acc.push(
                <Row className="mb-2" key={`row-${cur.id}`}>
                  <Col key={cur.id}>
                    <Card
                      className={clsx(
                        cardStyle,
                        selectedOccupationIds.includes(cur.reprId) && active
                      )}
                      onClick={() => toggle(cur.reprId)}
                    >
                      <Card.Body>
                        <b>
                          {cur.emoji} {cur.name}
                        </b>
                        <div className="text-muted">
                          {cur.description || cur.occupations.map((i) => i.name).join(', ')}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  {next && (
                    <Col key={next.id}>
                      <Card
                        className={clsx(
                          cardStyle,
                          selectedOccupationIds.includes(next.reprId) && active
                        )}
                        onClick={() => toggle(next.reprId)}
                      >
                        <Card.Body>
                          <b>
                            {next.emoji} {next.name}
                          </b>
                          <div className="text-muted">
                            {next.description || next.occupations.map((i) => i.name).join(', ')}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  )}
                </Row>
              )
            }
            return acc
          }, [])}
        </Container>
      )}
      <Stack direction="horizontal">
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={() => {
            navigate('/onboarding/basic-job-application-info', {
              state: { direction: PageTransition.FromLeft },
            })
          }}
        >
          ←
        </Button>
        <Button
          variant="outline-primary"
          size="lg"
          className="ms-auto"
          disabled={selectedOccupationIds.length < 3}
          onClick={() => {
            occupationsMutation.mutate(selectedOccupationIds)
          }}
        >
          →
        </Button>
      </Stack>
    </Container>
  )
}
